/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect, useRef } from 'react';

import useWindowSize from '../../hooks/useWindowSize';
import * as AppPropTypes from '../../lib/PropTypes';

import { useCurrentPage } from '../../contexts/PagesContext';
import { useMenu } from '../../contexts/SiteContext';
import MenuItem from './MenuItem';

import styles from '../../../styles/menus/main-menu.module.scss';

const propTypes = {
    items: AppPropTypes.menuItems.isRequired,
    minified: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    minified: false,
    className: null,
};

const MainMenu = ({ items, minified, className }) => {
    const hover = useRef(false);
    const [activeMenuId, setActiveMenuId] = useState(null);
    const { menuOpen, setMenuOpen, menuHidden } = useMenu();
    const { width: windowWidth } = useWindowSize();
    const page = useCurrentPage();

    const menuItems = items.map((item) => ({
        ...item,
        active: item !== null && page !== null && item.url === page.url,
    }));

    const onOpen = useCallback(
        ({ id }) => {
            hover.current = true;
            setActiveMenuId(id);
        },
        [hover, setActiveMenuId],
    );

    const onClose = useCallback(
        (immediate) => {
            hover.current = false;
            if (immediate) {
                setActiveMenuId(null);
            } else {
                setTimeout(() => {
                    if (!hover.current) {
                        setActiveMenuId(null);
                    }
                }, 300);
            }
        },
        [hover, setActiveMenuId],
    );

    useEffect(() => {
        setActiveMenuId(null);
    }, [menuOpen]);

    useEffect(() => {
        // on resize, just close everything
        setMenuOpen(false);
        setActiveMenuId(null);
    }, [windowWidth]);

    return (
        <nav
            className={classNames(styles.container, {
                [styles.visible]: !menuHidden,
                [className]: className !== null,
            })}
        >
            {menuItems.map((item) => (
                <MenuItem
                    className={styles.link}
                    key={item.id}
                    item={item}
                    minified={minified}
                    opened={item.id === activeMenuId}
                    large={item?.large || false}
                    onOpen={onOpen}
                    onClose={onClose}
                />
            ))}
        </nav>
    );
};

MainMenu.propTypes = propTypes;
MainMenu.defaultProps = defaultProps;

export default MainMenu;
